import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { personasAPI, semanasConfirmadasAPI, notificacionesAPI } from '../services/api';
import { format, addWeeks, startOfWeek, isBefore, isAfter } from 'date-fns';
import { es } from 'date-fns/locale';
import accionaLogo from '../assets/Color_Vectorizado 1.png';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [pendingTasks, setPendingTasks] = useState({
    count: 0,
    tasks: [],
    loading: false
  });
  const [lastCheckTime, setLastCheckTime] = useState(0); // Para evitar llamadas múltiples
  const [userPersonaCache, setUserPersonaCache] = useState(null); // Cache de la persona del usuario
  const [menciones, setMenciones] = useState({ count: 0, items: [] });
  const menuRef = useRef(null);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isActiveOperaciones = () => {
    const operacionesRoutes = ['/operaciones', '/kanban-operaciones', '/tabla-operaciones'];
    // Si estamos en una página de detalle o edición, verificar el parámetro 'from'
    if (location.pathname.startsWith('/projects/')) {
      return from === 'operaciones';
    }
    return operacionesRoutes.includes(location.pathname);
  };

  const isActiveDesarrolloNegocio = () => {
    const desarrolloRoutes = ['/desarrollo-negocio', '/kanban-comercial', '/tabla-desarrollo-negocio'];
    // Si estamos en una página de detalle o edición, verificar el parámetro 'from'
    if (location.pathname.startsWith('/projects/')) {
      return from === 'desarrollo_negocio';
    }
    return desarrolloRoutes.includes(location.pathname);
  };

  const getLinkStyle = (isActiveState) => ({
    textDecoration: 'none',
    color: isActiveState ? '#2c3e50' : '#6c757d',
    fontSize: '15px',
    fontWeight: isActiveState ? '600' : '400',
    padding: '8px 16px',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    position: 'relative',
    cursor: 'pointer'
  });

  const getLinkHoverStyle = () => ({
    ':hover': {
      color: '#2c3e50',
      backgroundColor: '#f8f9fa'
    }
  });

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Function to check for pending time tracking tasks
  const checkPendingTasks = async () => {
    const now = Date.now();
    
    // Evitar llamadas muy frecuentes (mínimo 30 segundos entre llamadas)
    if (now - lastCheckTime < 30000) {
      return;
    }
    
    if (!user || pendingTasks.loading) return;

    setPendingTasks(prev => ({ ...prev, loading: true }));
    setLastCheckTime(now);

    try {
      // Use cached user persona if available
      let currentUserPerson = userPersonaCache || user.persona;
      
      if (!currentUserPerson) {
        try {
          // Get current user's persona only if not in cache
          const currentUserResponse = await personasAPI.getCurrentUser();
          currentUserPerson = currentUserResponse.data.persona;
          // Guardar en caché
          setUserPersonaCache(currentUserPerson);
        } catch (personaError) {
          console.warn('Could not fetch current user persona:', personaError);
          setPendingTasks({ count: 0, tasks: [], loading: false });
          return;
        }
      }

      if (!currentUserPerson || !currentUserPerson.id) {
        setPendingTasks({ count: 0, tasks: [], loading: false });
        return;
      }

      // Get confirmed weeks for the user
      const confirmedWeekKeys = await semanasConfirmadasAPI.getConfirmedWeekKeys(currentUserPerson.id);
      const confirmedWeeksSet = new Set(confirmedWeekKeys);

      // Calculate weeks from December 2025 to current week
      const startValidationDate = new Date(2025, 11, 1); // December 1, 2025
      const today = new Date();
      const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday
      
      const pendingWeeks = [];
      let checkWeek = startOfWeek(startValidationDate, { weekStartsOn: 1 });

      while (isBefore(checkWeek, currentWeekStart) || format(checkWeek, 'yyyy-MM-dd') === format(currentWeekStart, 'yyyy-MM-dd')) {
        const weekKey = `${currentUserPerson.id}-${format(checkWeek, 'yyyy-MM-dd')}`;
        
        if (!confirmedWeeksSet.has(weekKey)) {
          pendingWeeks.push({
            weekStart: new Date(checkWeek),
            weekKey,
            description: `Completar semana del ${format(checkWeek, 'dd/MM/yyyy', { locale: es })}`
          });
        }

        checkWeek = addWeeks(checkWeek, 1);
      }

      // Sort by oldest first (most urgent)
      pendingWeeks.sort((a, b) => a.weekStart - b.weekStart);

      setPendingTasks({
        count: pendingWeeks.length,
        tasks: pendingWeeks,
        loading: false
      });

    } catch (error) {
      console.error('Error checking pending tasks:', error);
      setPendingTasks({ count: 0, tasks: [], loading: false });
    }
  };

  // Load mention notifications
  const checkMenciones = async () => {
    if (!user) return;
    try {
      const res = await notificacionesAPI.getMisNotificaciones();
      const items = res.data || [];
      const noLeidas = items.filter((n) => !n.leida);
      setMenciones({ count: noLeidas.length, items });
    } catch {
      // silent
    }
  };

  const handleMarcarMencionLeida = async (notifId) => {
    try {
      await notificacionesAPI.marcarLeida(notifId);
      setMenciones((prev) => ({
        ...prev,
        count: Math.max(0, prev.count - 1),
        items: prev.items.map((n) => n.id === notifId ? { ...n, leida: true } : n),
      }));
    } catch {
      // silent
    }
  };

  const handleMarcarTodasLeidas = async () => {
    try {
      await notificacionesAPI.marcarTodasLeidas();
      setMenciones((prev) => ({
        count: 0,
        items: prev.items.map((n) => ({ ...n, leida: true })),
      }));
    } catch {
      // silent
    }
  };

  // Function to handle notification click
  const handleNotificationClick = () => {
    if (pendingTasks.tasks.length > 0) {
      // Navigate to time-tracking page with the oldest pending week
      const oldestWeek = pendingTasks.tasks[0];
      const weekParam = format(oldestWeek.weekStart, 'yyyy-MM-dd');
      setShowUserMenu(false); // Close the menu
      navigate(`/time-tracking?week=${weekParam}`);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load pending tasks when user changes
  useEffect(() => {
    if (user) {
      // Limpiar caché cuando cambie el usuario
      setUserPersonaCache(null);
      setLastCheckTime(0);
      checkPendingTasks();
      checkMenciones();
      
      // Set up interval to check every 10 minutes (reducido de 5)
      const interval = setInterval(() => {
        checkPendingTasks();
        checkMenciones();
      }, 10 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [user?.id]); // Solo reaccionar cuando cambie el ID del usuario

  // Listen for page visibility changes to refresh notifications
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        // Page is now visible, refresh pending tasks
        checkPendingTasks();
      }
    };

    const handleFocus = () => {
      if (user) {
        // Window gained focus, refresh pending tasks
        checkPendingTasks();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);

  // Listen for location changes to refresh notifications when returning from time-tracking
  useEffect(() => {
    if (user) {
      checkPendingTasks();
    }
  }, [location.pathname, user]);

  return (
    <>
      {/* CSS for notification badge animation */}
      <style>
        {`
          .container {
            max-width: 95% !important;
          }
          
          @keyframes pulse-red {
            0% {
              box-shadow: 0 2px 6px rgba(220, 53, 69, 0.4);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 2px 12px rgba(220, 53, 69, 0.8);
              transform: scale(1.05);
            }
            100% {
              box-shadow: 0 2px 6px rgba(220, 53, 69, 0.4);
              transform: scale(1);
            }
          }
          
          .notification-badge {
            background-color: #dc3545 !important;
            color: white !important;
          }
          
          .notification-badge:hover {
            background-color: #c82333 !important;
            animation: pulse-red 2s infinite;
          }
        `}
      </style>
      
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: '#fafafae6',
        borderBottom: '1px solid #e9ecef',
        padding: '1rem',
        zIndex: 1050,
        backdropFilter: 'blur(10px)'
      }}>
      <div className="container">
        <div className="d-flex justify-content-between align-items-center">
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img 
              src={accionaLogo} 
              alt="Acciona Logo" 
              style={{ 
                height: '35px',
                width: 'auto'
              }} 
            />
            <h1 style={{ 
              margin: 0, 
              color: '#2c3e50', 
              fontSize: '1.6rem',
              fontWeight: '700',
              letterSpacing: '-0.5px'
            }}>
              ACCIONA - Digital Hub
            </h1>
          </Link>
          
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center" style={{ gap: '4px' }}>
              <Link 
                to="/" 
                style={getLinkStyle(isActive('/'))}
                onMouseEnter={(e) => {
                  if (!isActive('/')) {
                    e.target.style.color = '#2c3e50';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/')) {
                    e.target.style.color = '#6c757d';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Inicio
              </Link>
              
              <Link 
                to="/desarrollo-negocio" 
                style={getLinkStyle(isActiveDesarrolloNegocio())}
                onMouseEnter={(e) => {
                  if (!isActiveDesarrolloNegocio()) {
                    e.target.style.color = '#2c3e50';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActiveDesarrolloNegocio()) {
                    e.target.style.color = '#6c757d';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Desarrollo de Negocio
              </Link>
              <Link 
                to="/innovation" 
                style={getLinkStyle(isActive('/innovation'))}
                onMouseEnter={(e) => {
                  if (!isActive('/innovation')) {
                    e.target.style.color = '#2c3e50';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/innovation')) {
                    e.target.style.color = '#6c757d';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Innovación
              </Link>
              
              <Link 
                to="/operaciones" 
                style={getLinkStyle(isActiveOperaciones())}
                onMouseEnter={(e) => {
                  if (!isActiveOperaciones()) {
                    e.target.style.color = '#2c3e50';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActiveOperaciones()) {
                    e.target.style.color = '#6c757d';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Operaciones
              </Link>
              
              <Link 
                to="/hitos-facturacion" 
                style={getLinkStyle(isActive('/hitos-facturacion'))}
                onMouseEnter={(e) => {
                  if (!isActive('/hitos-facturacion')) {
                    e.target.style.color = '#2c3e50';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/hitos-facturacion')) {
                    e.target.style.color = '#6c757d';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Facturación
              </Link>

              {user?.can_view_pedidos && (
                <Link
                  to="/presupuesto"
                  style={getLinkStyle(isActive('/presupuesto') || location.pathname.startsWith('/pedidos'))}
                  onMouseEnter={(e) => {
                    if (!isActive('/presupuesto')) {
                      e.target.style.color = '#2c3e50';
                      e.target.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive('/presupuesto')) {
                      e.target.style.color = '#6c757d';
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  Presupuesto
                </Link>
              )}
              
              {/* <Link 
                to="/analisis-costes" 
                style={getLinkStyle(isActive('/analisis-costes'))}
                onMouseEnter={(e) => {
                  if (!isActive('/analisis-costes')) {
                    e.target.style.color = '#2c3e50';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/analisis-costes')) {
                    e.target.style.color = '#6c757d';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Análisis de Costes
              </Link>
              
              <Link 
                to="/people" 
                style={getLinkStyle(isActive('/people'))}
                onMouseEnter={(e) => {
                  if (!isActive('/people')) {
                    e.target.style.color = '#2c3e50';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/people')) {
                    e.target.style.color = '#6c757d';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Personas
              </Link>
               */}
              <Link 
                to="/time-tracking" 
                style={getLinkStyle(isActive('/time-tracking'))}
                onMouseEnter={(e) => {
                  if (!isActive('/time-tracking')) {
                    e.target.style.color = '#2c3e50';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/time-tracking')) {
                    e.target.style.color = '#6c757d';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Imputar Horas
              </Link>
            </div>
            
            {/* User menu */}
            <div style={{ position: 'relative', marginLeft: '24px' }} ref={menuRef}>
              <button 
                onClick={toggleUserMenu}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#6c757d',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#2c3e50';
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#6c757d';
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                {/* Notification badge - positioned as superscript */}
                {(pendingTasks.count + menciones.count) > 0 && (
                  <div 
                    className="notification-badge"
                    style={{
                      position: 'absolute',
                      top: '0px',
                      left: '-5px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(220, 53, 69, 0.4)',
                      border: '2px solid white',
                      zIndex: 1
                    }}
                    title={`${pendingTasks.count + menciones.count} notificaciones pendientes`}
                  >
                    {(pendingTasks.count + menciones.count) > 9 ? '9+' : (pendingTasks.count + menciones.count)}
                  </div>
                )}
                
                <span>{user?.first_name || user?.username || 'Usuario'}</span>
                
                <span style={{ fontSize: '10px', opacity: 0.7 }}>
                  {showUserMenu ? '▲' : '▼'}
                </span>
              </button>
              
              {showUserMenu && (
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: '8px',
                  backgroundColor: 'white',
                  border: '1px solid #e9ecef',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  minWidth: '220px',
                  zIndex: 1000,
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    padding: '16px 20px', 
                    borderBottom: '1px solid #f1f3f4',
                    fontSize: '14px'
                  }}>
                    <div style={{ 
                      fontWeight: '600', 
                      color: '#2c3e50',
                      marginBottom: '4px' 
                    }}>
                      {user?.first_name && user?.last_name ? 
                        `${user.first_name} ${user.last_name}` : 
                        user?.username}
                    </div>
                    <div style={{ color: '#6c757d', fontSize: '13px' }}>
                      {user?.email}
                    </div>
                  </div>
                  
                  {/* Pending tasks section */}
                  {pendingTasks.count > 0 && (
                    <div style={{ 
                      padding: '12px 20px', 
                      borderBottom: '1px solid #f1f3f4',
                      backgroundColor: '#fff3cd'
                    }}>
                      <div style={{ 
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#856404',
                        marginBottom: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {pendingTasks.count > 9 ? '9+' : pendingTasks.count}
                        </span>
                        Tareas Pendientes
                      </div>
                      <button
                        onClick={handleNotificationClick}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#856404',
                          fontSize: '12px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          padding: '0',
                          textDecoration: 'underline',
                          width: '100%'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#533a03';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#856404';
                        }}
                      >
                        {pendingTasks.count === 1 ? 
                          '1 semana de horas por completar' :
                          `${pendingTasks.count} semanas de horas por completar`
                        }
                        <br />
                        <span style={{ fontSize: '11px', opacity: 0.8 }}>
                          Click para ir a la más antigua
                        </span>
                      </button>
                    </div>
                  )}

                  {/* Sección menciones */}
                  {menciones.items.length > 0 && (
                    <div style={{
                      padding: '12px 20px',
                      borderBottom: '1px solid #f1f3f4',
                      maxHeight: '260px',
                      overflowY: 'auto',
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <span>
                          {menciones.count > 0 && (
                            <span style={{
                              backgroundColor: '#4a90d9',
                              color: 'white',
                              borderRadius: '10px',
                              padding: '1px 6px',
                              fontSize: '10px',
                              marginRight: '6px',
                            }}>
                              {menciones.count}
                            </span>
                          )}
                          Menciones
                        </span>
                        {menciones.count > 0 && (
                          <button
                            onClick={handleMarcarTodasLeidas}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#6c757d',
                              fontSize: '11px',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0,
                            }}
                          >
                            Marcar todas leídas
                          </button>
                        )}
                      </div>
                      {menciones.items.map((n) => {
                        const link = n.iniciativa
                          ? `/projects/${n.iniciativa}`
                          : null;
                        return (
                          <div
                            key={n.id}
                            style={{
                              padding: '6px 8px',
                              marginBottom: '4px',
                              borderRadius: '6px',
                              backgroundColor: n.leida ? 'transparent' : '#eef4ff',
                              border: n.leida ? '1px solid #f1f3f4' : '1px solid #cce0ff',
                              fontSize: '12px',
                            }}
                          >
                            <div style={{ color: '#2c3e50', marginBottom: '2px' }}>
                              <strong>{n.autor_nombre}</strong> te mencionó en{' '}
                              {link ? (
                                <a
                                  href={link}
                                  onClick={() => {
                                    handleMarcarMencionLeida(n.id);
                                    setShowUserMenu(false);
                                  }}
                                  style={{ color: '#4a90d9', textDecoration: 'underline' }}
                                >
                                  {n.iniciativa_nombre || n.accion_dn_nombre || 'un comentario'}
                                </a>
                              ) : (
                                <span>{n.accion_dn_nombre || 'un comentario'}</span>
                              )}
                            </div>
                            <div style={{ color: '#6c757d', fontSize: '11px', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px' }}>
                              "{n.texto_preview?.slice(0, 80)}{n.texto_preview?.length > 80 ? '…' : ''}"
                            </div>
                            {!n.leida && (
                              <button
                                onClick={() => handleMarcarMencionLeida(n.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#4a90d9',
                                  fontSize: '10px',
                                  cursor: 'pointer',
                                  padding: 0,
                                  marginTop: '2px',
                                  textDecoration: 'underline',
                                }}
                              >
                                Marcar leída
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <button 
                    onClick={handleLogout}
                    style={{ 
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      color: '#dc3545',
                      padding: '14px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8d7da';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Navbar;